import { FC, useContext } from "react";
import { Anchor, Box, Flex, Text, useMantineTheme } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { Link } from "react-router-dom";
import { CompetitionContext } from "../../../../CompetitionContext";
import { useUpcomingGames } from "../../../../hooks/useUpcomingGames";
import { useCompetition } from "../../../../hooks/useCompetition";
import { TeamLogo } from "../../../../Components/TeamLogo";
import { TeamName } from "../../../../Components/TeamName";
import { IconChevronRight, IconClock } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { components } from "../../../../openapi/generated-types";
import { CardContainer } from "../../../../Components/CardContainer";
import { WidgetHeader } from "../../../../Components/WidgetHeader";

import "@mantine/carousel/styles.css";

interface UpcomingGameCardProps {
  game: components["schemas"]["Game"];
  matchdayName?: string;
}

const UpcomingGameCard: FC<UpcomingGameCardProps> = ({
  game,
  matchdayName,
}) => {
  const theme = useMantineTheme();

  const { homeTeamId, guestTeamId } = game.actualTeams;
  const gameDateTime = DateTime.fromISO(game.dateTime);

  return (
    <CardContainer p="sm" h="100%">
      {/* Header with matchday */}
      <Flex justify="space-between" align="center" mb="xs">
        <Text size="xs" c="dimmed" fw={500} lineClamp={1}>
          {matchdayName ?? game.name}
        </Text>
      </Flex>

      {/* Teams and Time */}
      <Flex align="center" gap="xs">
        {/* Home Team */}
        {homeTeamId ? (
          <Anchor
            component={Link}
            to={`/teams/${homeTeamId}`}
            underline="never"
            c="inherit"
            style={{ flex: 1 }}
          >
            <Flex direction="column" align="center" gap={4}>
              <TeamLogo teamId={homeTeamId} height={36} />
              <Text size="xs" ta="center" lineClamp={1} fw={500}>
                <TeamName teamId={homeTeamId} short />
              </Text>
            </Flex>
          </Anchor>
        ) : (
          <Flex direction="column" align="center" gap={4} style={{ flex: 1 }}>
            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: theme.colors.gray[2],
              }}
            />
            <Text size="xs" c="dimmed" ta="center">
              TBD
            </Text>
          </Flex>
        )}

        {/* Time */}
        <Flex direction="column" align="center">
          <IconClock size={16} color={theme.colors.gray[5]} />
          <Text size="xs" fw={600} mt={2}>
            {gameDateTime.toFormat("HH:mm")}
          </Text>
        </Flex>

        {/* Guest Team */}
        {guestTeamId ? (
          <Anchor
            component={Link}
            to={`/teams/${guestTeamId}`}
            underline="never"
            c="inherit"
            style={{ flex: 1 }}
          >
            <Flex direction="column" align="center" gap={4}>
              <TeamLogo teamId={guestTeamId} height={36} />
              <Text size="xs" ta="center" lineClamp={1} fw={500}>
                <TeamName teamId={guestTeamId} short />
              </Text>
            </Flex>
          </Anchor>
        ) : (
          <Flex direction="column" align="center" gap={4} style={{ flex: 1 }}>
            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: theme.colors.gray[2],
              }}
            />
            <Text size="xs" c="dimmed" ta="center">
              TBD
            </Text>
          </Flex>
        )}
      </Flex>
    </CardContainer>
  );
};

export const UpcomingGames: FC<{ maxGames?: number }> = ({ maxGames = 6 }) => {
  const competitionId = useContext(CompetitionContext);

  const upcomingGames = useUpcomingGames(competitionId, false)?.slice(
    0,
    maxGames,
  );
  const competition = useCompetition(competitionId);

  if (!upcomingGames || upcomingGames.length === 0) {
    return null;
  }

  // Create a map of matchday IDs to names
  const matchdayMap = new Map<string, string>();
  if (competition.isSuccess) {
    for (const matchday of competition.data.matchdays) {
      matchdayMap.set(matchday.id, matchday.name);
    }
  }

  return (
    <Box>
      <WidgetHeader
        title="Nächste Spiele"
        order={3}
        linkText="Alle Spiele"
        linkTo="/games"
      />
      <Carousel
        slideSize={{ base: "70%", sm: "45%", md: "30%" }}
        slideGap="sm"
        withControls={false}
      >
        {upcomingGames.map((game) => (
          <Carousel.Slide key={game.id}>
            <UpcomingGameCard
              game={game}
              matchdayName={
                game.matchdayId ? matchdayMap.get(game.matchdayId) : undefined
              }
            />
          </Carousel.Slide>
        ))}
        <Carousel.Slide>
          <Anchor component={Link} to="/games" underline="never">
            <CardContainer p="sm" h="100%">
              <Flex
                direction="column"
                align="center"
                justify="center"
                h="100%"
                gap="xs"
              >
                <IconChevronRight
                  size={24}
                  color="var(--mantine-color-dimmed)"
                />
                <Text size="sm" c="dimmed" fw={500}>
                  Alle Spiele
                </Text>
              </Flex>
            </CardContainer>
          </Anchor>
        </Carousel.Slide>
      </Carousel>
    </Box>
  );
};
